import { ClipboardCopyIcon } from "@heroicons/react/outline";
import { CheckIcon } from "@heroicons/react/solid";
import copy from "copy-to-clipboard";
import { useState } from "react";

import IntegrationDetailLayout from "@apps/org-settings/components/integrations/integration-detail-layout";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";

import { MCP_DOCS_URL, getMcpServerUrl } from "./mcp-constants";

const CopyField = ({
  label,
  value,
  multiline = false,
}: {
  label: string;
  value: string;
  multiline?: boolean;
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    copy(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mt-3">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium uppercase tracking-wide text-gray-400">
          {label}
        </span>
        <button
          type="button"
          onClick={handleCopy}
          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
          aria-label={`Copy ${label}`}
        >
          {copied ? (
            <CheckIcon className="h-4 w-4 text-green-500" />
          ) : (
            <ClipboardCopyIcon className="h-4 w-4" />
          )}
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>
      {multiline ? (
        <pre className="overflow-x-auto rounded-sm border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700">
          <code>{value}</code>
        </pre>
      ) : (
        <code className="block break-all rounded-sm border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
          {value}
        </code>
      )}
    </div>
  );
};

const McpIntegrationDialog = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const mcpUrl = getMcpServerUrl(window.location.origin);
  const jsonConfig = JSON.stringify(
    { mcpServers: { topicflow: { type: "http", url: mcpUrl } } },
    null,
    2,
  );

  return (
    <IntegrationDetailLayout
      open={open}
      onClose={onClose}
      name="AI Tools"
      logo=""
      logoNode={<TopicflowIcon type="ai" className="h-10 w-10 text-blue-500" />}
      description={
        <p>
          Connect Topicflow to your AI assistant with our MCP (Model Context
          Protocol) server. Once connected, your assistant can read and update
          your goals, meetings, feedback, and more — after you approve access.
        </p>
      }
      actions={
        <Button
          theme={buttonTheme.primary}
          url={MCP_DOCS_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          View setup guide
        </Button>
      }
    >
      <CopyField label="Connection URL" value={mcpUrl} />
      <CopyField label="JSON config" value={jsonConfig} multiline />
      <p className="text-sm text-gray-400">
        Works with Claude, ChatGPT, Notion, GitHub Copilot, Gemini, and other
        MCP clients.
      </p>
    </IntegrationDetailLayout>
  );
};

export default McpIntegrationDialog;
